import type { SettingsListActionRef } from "../../../components/SettingsListPage";
import { FilterOutlined,PlusOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MerchantPaymentLinkAction } from "../../../../../../../shared/frontend/payments/MerchantPaymentLinkAction";
import { Alert,Button,Flex,Grid,Switch,Typography } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import type { UnitsSettings } from "../../../app/units/units";
import { FormDrawer } from "../../../components/FormDrawer";
import { SettingsListPage,type ProColumnsWithMeasure } from "../../../components/SettingsListPage";
import { createPersonWalletColumns,PERSON_WALLET_COLUMN_DEFINITIONS } from "./personWalletColumns";
import type { WalletRow } from "./personDetailTypes";
import { personDetailQueryKeys } from "./personDetailQueryKeys";
import { usePersonAggregatorRequirements } from "../../../app/integrations/usePersonAggregatorIdentities";
import { ContractAggregatorSection } from "./ContractAggregatorSection";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

type Props = {
  api: Pick<ApiClient, "get" | "post">;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  personId: string;
  canWriteFinance: boolean;
  showHistoricalWallets: boolean;
  setShowHistoricalWallets: (value: boolean) => void;
  walletsRef: React.RefObject<SettingsListActionRef | null>;
  onOpenWalletCreate: () => void;
  onOpenWalletEdit: (wallet: WalletRow) => void;
};

export function PersonWalletsTab({
  api,
  text,
  units,
  personId,
  canWriteFinance,
  showHistoricalWallets,
  setShowHistoricalWallets,
  walletsRef,
  onOpenWalletCreate,
  onOpenWalletEdit
}: Props) {
  const queryClient = useQueryClient();
  const aggregatorRequirements = usePersonAggregatorRequirements(personId);
  const walletPaymentOptions = useQuery({ queryKey: ["person", personId, "merchant-wallet-options"],
    queryFn: () => api.get<string[]>(`/api/v1/persons/${personId}/merchant-payment-links/wallet-options`), enabled: canWriteFinance });
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const useStackedToolbar = screens.sm === false;
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const walletsAllColumnsMap: Record<string, ListColumn<WalletRow>> = React.useMemo(
    () => createPersonWalletColumns(text, units),
    [text, units]
  );

  const requestWallets = React.useCallback(async () => {
    const list = await queryClient.fetchQuery({
      queryKey: personDetailQueryKeys.person.wallets(personId),
      queryFn: () => api.get<WalletRow[]>(`/api/v1/wallets?personId=${encodeURIComponent(personId)}`),
      staleTime: 0
    });
    const today = dayjs().startOf("day");
    let filtered = list || [];
    if (!showHistoricalWallets) {
      filtered = filtered.filter((r) => {
        const isInactive = r?.isActive === false;
        const validToIso = String(r?.validTo || "").slice(0, ISO_DATE_LENGTH);
        const validTo = validToIso ? dayjs(validToIso) : null;
        const isExpired = !!validTo && validTo.isValid() && validTo.isBefore(today, "day");
        return !(isInactive || isExpired);
      });
    }
    return { data: filtered, success: true, total: filtered.length };
  }, [queryClient, api, personId, showHistoricalWallets]);

  return (
    <>
      {aggregatorRequirements.isError && <Alert type="error" title={text("Aggregator identities unavailable")}
        action={<Button onClick={() => aggregatorRequirements.refetch()}>{text("Retry")}</Button>} />}
      <SettingsListPage<WalletRow>
        toolbarExtra={useStackedToolbar ? undefined : (
          <Flex className="person-detail-toolbar-switch" justify="flex-start" align="center" gap={8} style={{ width: isMobile ? "100%" : undefined, minWidth: 0 }}>
            <Typography.Text style={{ minWidth: 0 }}>{text("Historical wallets")}</Typography.Text>
            <Switch checked={showHistoricalWallets} onChange={setShowHistoricalWallets} />
          </Flex>
        )}
        toolbarRightExtra={(useStackedToolbar || canWriteFinance) ? (
          <Flex wrap="wrap" gap={8} justify="flex-end" align="center" style={{ width: "100%", minWidth: 0 }}>
            {useStackedToolbar ? (
              <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerOpen(true)}>
                {text("Filter")}
              </Button>
            ) : null}
            {canWriteFinance ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={onOpenWalletCreate}>
                {text("Add wallet")}
              </Button>
            ) : null}
          </Flex>
        ) : undefined}
        storageKey="persons.detail.wallets"
        expandable={{
          rowExpandable: row => !!aggregatorRequirements.data?.some(x => x.walletId === row.id),
          render: row => <ContractAggregatorSection personId={personId}
            requirements={(aggregatorRequirements.data ?? []).filter(x => x.walletId === row.id)} />
        }}
        rowKey="id"
        actionRef={walletsRef}
        request={requestWallets}
        columnDefinitions={PERSON_WALLET_COLUMN_DEFINITIONS}
        allColumnsMap={walletsAllColumnsMap as Record<string, ProColumnsWithMeasure<WalletRow>>}
        actionsColumn={canWriteFinance ? {
          title: "",
          key: "actions",
          valueType: "option",
          fixed: "right" as const,
          align: "right" as const,
          render: (_: unknown, r: WalletRow) => (
            <Flex wrap="wrap">
            {walletPaymentOptions.data?.includes(r.id) && <MerchantPaymentLinkAction tr={text} amountRequired
              create={amount => api.post(`/api/v1/persons/${personId}/merchant-payment-links`, { subject: "Wallet", subjectId: r.id, amount })} />}
            </Flex>
          )
        } : undefined}
        onRowClick={onOpenWalletEdit}
      />
      <FormDrawer mode="retained" dirty={false}
        title="Filter"
        open={filterDrawerOpen}
        onCancel={() => setFilterDrawerOpen(false)}

        cancelText="Close"
        autoFocusFirstField={false}
      >
        <Flex className="person-detail-toolbar-switch" justify="space-between" align="center" gap={8} style={{ minWidth: 0, width: "100%" }}>
          <Typography.Text style={{ minWidth: 0 }}>{text("Historical wallets")}</Typography.Text>
          <Switch checked={showHistoricalWallets} onChange={setShowHistoricalWallets} />
        </Flex>
      </FormDrawer>
    </>
  );
}
